/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  MouseEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import debounce from 'lodash.debounce';
import DeviceGroupsChip from '@ion/applications/src/pages/devices/DeviceGroupsChip';
import {translate} from '@ion/core/src/locale/locale';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  Account,
  Device,
  EntityStatus,
  GenericObject,
  Link
} from '@ion/core/src/types/entities';
import {smoothScrollByElement} from '@ion/core/src/utils/BrowserUtils';
import {fahrenheitToCelsius} from '@ion/core/src/utils/Conversions';
import {toISOString} from '@ion/core/src/utils/DateUtil';
import {isObjectEmpty} from '@ion/core/src/utils/ObjectUtil';
import {
  isFirstCharSpace,
  isValue,
  removeStringExtraSpace
} from '@ion/core/src/utils/StringUtil';
import {isPositiveInteger} from '@ion/core/src/utils/Validators';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import {FormIntegerField} from '@ion/dls/src/components/forms/FormIntegerField';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormSection} from '@ion/dls/src/components/forms/FormSection';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import Switch from '@ion/dls/src/components/switch/Switch';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {useFormSave} from '@ion/dls/src/hooks/useFormSave';
import {
  FormState,
  useFormState
} from '@ion/dls/src/hooks/useFormState';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {useMergeState} from '@ion/dls/src/hooks/useMergeState';
import {InfoIcon} from '@ion/dls/src/icons/InfoIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import DeviceConfigurationChooser from '@ion/features/src/entities/device/DeviceConfigurationChooser';
import DeviceTypeChooser from '@ion/features/src/entities/device/DeviceTypeChooser';
import {LabelTypography} from '../typography/LabelTypography';
import DatePicker from './datetimepicker/DatePickerExporter';
import {
  deserialize,
  DeviceFormFields,
  getInitialState,
  getSpreadVal,
  isAutoFlightMode,
  isFightModeDevice,
  isOceanModeDevice,
  isSCIGPSDevice,
  isSCIMovingDevice,
  isSCIorCMFDevice,
  isSCITagDevice,
  isSCITemperatureDevice,
  isSelectedDeviceOEM,
  isVisionDevice,
  SCIDeviceSettingsFormField,
  SCIDeviceSettingsUnits,
  serialize,
  SerializedArgs,
  TargetType,
  TemperatureControlProps
} from './DeviceEditorUtils';
import DeviceThresholdsChooser from './DeviceThresholdsChooser';
import EditorDialog from './EditorDialog';
import styles from './editorStyles';
import EntityStatusChooser from './EntityStatusChooser';
import GroupChooser, {DisableAccountChooser} from './GroupChooser';
import ReportingRateChooser from './ReportingRateChooser';
import SpreaderChooser from './SpreaderChooser';

export interface DeviceEditorOptions extends DisableAccountChooser {
  includeAdditionalDeviceFields?: boolean;
  isDeviceNotes?: boolean;
  includeGPSFields?: boolean;
  includeDatetimeInformation?: boolean;
  includeIdentityInformation?: boolean;
  showSCISettings?: boolean;
  showDeviceType?: boolean;
  includeSpreaderChooser?: boolean;
}

export interface DeviceEditorProps extends DeviceEditorOptions {
  onSwitchToBulkAdd?: (e: MouseEvent<HTMLButtonElement, MouseEvent>, id?: number) => void;
  onCloseDialog?: (event: SyntheticEvent<{}, Event>) => void;
  onSubmit?: (item: GenericObject) => void;
  device?: Device | GenericObject;
  account: Account | Link;
  systemOfUnits?: string;
  includeGroupProperties?: boolean;
  open: boolean;
  isViewDialog: boolean;
  includeSwapDevice?: boolean;
  includeGroups?: boolean;
  enableBulkAdd?: boolean;
  clear?: boolean;
  loading?: boolean;
  defaultLabel?: boolean;
  isMultiSelectSpreader?: boolean;
};

const overrides = () => ({
  overrides : {
    marginTop : '16px',
    padding   : '0 16px'
  },
  switchMargin : {
    marginTop : '16px'
  },
  notificationPreference : {
    display         : 'flex',
    justifyContent  : 'space-between',
    backgroundColor : 'white',
    padding         : 10,
    alignItems      : 'center'
  },
  notificationPreferenceExpanded : {
    backgroundColor : 'white',
    marginTop       : '2px',
    padding         : '10px 20px 10px 20px',
    display         : 'flex',
    flexDirection   : 'column'
  },
  threshHolderSubContainer : {
    display        : 'flex',
    justifyContent : 'space-between',
    alignItems     : 'center'
  },
  threshHolderFieldContainer : {
    display       : 'flex',
    flexDirection : 'column',
    alignItems    : 'flex-start',
    marginLeft    : '1rem'
  },
  threshHolderErrorContainer : {
    color      : dlsColor('caRed'),
    fontFamily : 'Roboto',
    fontSize   : '12px',
    marginTop  : '1rem',
    marginLeft : '1rem'
  },
  bulkAddLegend : {
    textAlign : 'right'
  },
  bulkAddCtaContainer : {
    marginRight : -10
  }
});

/**
 * The Device editor with set fields and encapsulated logic for handling the form's state,
 * including validation.
 *
 * @param {boolean} open: Determines if the DeviceEditor dialog should be open
 * @param {object} account: User's account object
 * @param {Function} onCloseDialog: Callback function fired when the dialog requests to be closed
 * @param {Function} onSubmit: Function the be run when the form is submitted.
 *   The function is passed an Account object
 * @param {object} device: Device that may be passed to the form (like in the case of editing)
 * @param {boolean} clear: If true we clear the form state
 * @param {boolean} loading: disables any for submission while the form is busy
 */
export const DeviceEditor = ({
  onSwitchToBulkAdd,
  onCloseDialog,
  onSubmit,
  account,
  systemOfUnits,
  open,
  device = {},
  clear = false,
  loading = false,
  includeAdditionalDeviceFields = false,
  isDeviceNotes = false,
  includeGPSFields = false,
  includeDatetimeInformation = false,
  includeIdentityInformation = false,
  includeSpreaderChooser = false,
  showSCISettings = false,
  isViewDialog = false,
  disableAccountChooser = false,
  includeSwapDevice = false,
  enableBulkAdd = false,
  defaultLabel = false,
  includeGroups = false,
  includeGroupProperties = true,
  showDeviceType = false,
  isMultiSelectSpreader = false
}: DeviceEditorProps) => {
  const editorStyles                          = useExtendStyles(styles, overrides);
  const classes                               = editorStyles();
  const {isPhone}                             = useMedia();
  const [openDatePickers, setOpenDatePickers] = useMergeState();
  const [groupValue, setGroupValue]           = useState(null);
  const [groupsLoading, setGroupsLoading]     = useState<boolean>(false);
  const [isValid, setIsValid]                 = useState<boolean>(true);
  const [checkSCIDevice, setIsCheckSCIDevice] = useState<boolean>(false);
  const [checkOEMDevice, setIsCheckOEMDevice] = useState<boolean>(false);
  const [label, setLabel]                     = useState(defaultLabel);
  const isAdd                                 = !device?.id;
  const [formState, setFormState]             = useFormState(isAdd
    ? getInitialState(includeAdditionalDeviceFields, device, includeSwapDevice)
    : deserialize(device, includeSwapDevice)
  );
  const [selectedGroups, setSelectedGroups]   = useState<Link[]>([]);
  const isEditMode                            = !isObjectEmpty(device);
  const isViewMode                            = isViewDialog || false;
  const entityLabel                           = {entityLabel: translate('common:entityLabels.device')};
  const sciGPSDevice                          = isSCIGPSDevice(device);
  const sciTagDevice                          = isSCITagDevice(device);
  const sciTemperatureDevice                  = isSCITemperatureDevice(device);
  const sciMovingDevice                       = isSCIMovingDevice(device);
  const flightModeDevice                      = isFightModeDevice(device);
  const autoFlightModeDevice                  = isAutoFlightMode(device);
  const oceanModeDevice                       = isOceanModeDevice(device);

  const [checkVisionDevice, setCheckVisionDevice] = useState<boolean>(false);

  const getVal = (field: string): boolean => {
    return formState[field]?.value;
  };

  const gpsActivated             = getVal(SCIDeviceSettingsFormField.GPSActivated);
  const tagEnabled               = getVal(SCIDeviceSettingsFormField.iOnTagEnabled);
  const temperatureAlert         = getVal(SCIDeviceSettingsFormField.TemperatureAlert);
  const tipAlert                 = getVal(SCIDeviceSettingsFormField.TipAlert);
  const lightLevelAlert          = getVal(SCIDeviceSettingsFormField.LightLevelAlert);
  const impactAlert              = getVal(SCIDeviceSettingsFormField.ImpactAlert);
  const oceanMode                = getVal(SCIDeviceSettingsFormField.oceanMode);
  const autoFlightMode           = getVal(SCIDeviceSettingsFormField.autoFlightMode);
  const pressureSensorFlightMode = getVal(SCIDeviceSettingsFormField.pressureSensorFlightMode);
  const esnVal                   = getVal(DeviceFormFields.ESN);
  const imeiVal                  = getVal(DeviceFormFields.IMEI);
  const imei2Val                 = getVal(DeviceFormFields.IMEI2);
  const serialNumberVal          = getVal(DeviceFormFields.SerialNumber);

  const serializer = function(data: FormState): GenericObject {
    const options = {
      state      : data,
      account    : account,
      isEditMode : isEditMode
    } as SerializedArgs;
    return serialize(options);
  };

  const dialogTitle = useMemo(() => {
    const entry = isEditMode ? (isViewDialog ? 'view' : (includeSwapDevice ? 'swap' : 'edit')) : 'add';
    return translate(`common:editors.shared.${entry}`, entityLabel);
  }, [isEditMode, isViewDialog, includeSwapDevice]);

  const submitValidation = {
    [DeviceFormFields.DeviceType] : ({value}) => {
      return includeAdditionalDeviceFields ? !!value : true;
    },
    [DeviceFormFields.DeviceConfiguration] : ({value}) => {
      const deviceType = formState[DeviceFormFields.DeviceType]?.value;
      return includeAdditionalDeviceFields ? !!(deviceType && value) : true;
    },
    [DeviceFormFields.ESN] : ({value}) => {
      const deviceType = formState[DeviceFormFields.DeviceType]?.value;
      return includeAdditionalDeviceFields ? (!!(deviceType && value) && isPositiveInteger(value)) : true;
    }
  };

  const submitValidationForSCI = {
    [DeviceFormFields.DeviceType] : ({value}) => {
      return includeAdditionalDeviceFields ? !!value : true;
    },
    [DeviceFormFields.DeviceConfiguration] : ({value}) => {
      const deviceType = formState[DeviceFormFields.DeviceType]?.value;
      return includeAdditionalDeviceFields ? !!(deviceType && value) : true;
    },
    [DeviceFormFields.SerialNumber] : ({value}) => {
      return includeAdditionalDeviceFields ? checkVisionDevice ? true : !!value : true;
    },
    [DeviceFormFields.IMEI] : ({value}) => {
      return includeAdditionalDeviceFields && checkVisionDevice ? !!value : true;
    }
  };

  const handleSubmit = useFormSave({
    validate  : (checkSCIDevice || checkOEMDevice) ? submitValidationForSCI : submitValidation,
    serialize : serializer,
    onSave    : onSubmit,
    formState,
    setFormState
  });

  useEffect(() => {
    if (!isObjectEmpty(device) && device?.id) {
      const serializedDevice = deserialize(device, includeSwapDevice);
      setFormState(serializedDevice);
      setCheckVisionDevice(isVisionDevice(device));
      setIsCheckSCIDevice(isSCIorCMFDevice(device));
      setIsCheckOEMDevice(isSelectedDeviceOEM(device));
      setSelectedGroups(serializedDevice.groups?.value);
    }
  }, [device, open]);

  useEffect(() => {
    if (clear) {
      clearForm();
    }
  }, [clear]);

  function clearForm() {
    setFormState({}, true);
    setSelectedGroups([]);
  };

  if (formState[DeviceFormFields.DeviceName]?.error) {
    smoothScrollByElement('editor');
  }

  const debounceFieldChange = debounce((target: TargetType, temperatureProps: TemperatureControlProps) => {
    const {name, value, required = false, error} = target;
    const trimmedValue = removeStringExtraSpace(value);
    const updatedValue = temperatureProps?.isFahrenheit
      ? fahrenheitToCelsius(parseFloat(trimmedValue), 3) : parseFloat(trimmedValue);
    setFormState({
      [name] : {
        name,
        value   : temperatureProps?.isNumber ? updatedValue : trimmedValue,
        required,
        error,
        changed : true
      }
    });
  }, 400);

  function handleFieldChange(e: ChangeEvent<HTMLInputElement>, temperatureProps?: TemperatureControlProps) {
    e.persist?.();
    const {value =''} = e?.target || {};
    if (value!=='' && isFirstCharSpace(value)) {
      e.target.value = value.trim();
    }
    debounceFieldChange(e.target, temperatureProps);
  };

  function handleChooserChange({name, value, required = false, changed = false}) {
    const deviceType = DeviceFormFields.DeviceType;
    let oemDevice    = false;
    let sciDevice    = false;
    let visionDevice = false;
    if (name === deviceType) {
      oemDevice    = isSelectedDeviceOEM(value);
      sciDevice    = isSCIorCMFDevice(value);
      visionDevice = isVisionDevice(value);
      setCheckVisionDevice(visionDevice);
      setIsCheckSCIDevice(sciDevice);
      setIsCheckOEMDevice(oemDevice);
      setLabel(false);
    }
    if (name === DeviceFormFields.DeviceType && value?.name !== device?.type?.title) {
      const serialNumberFlag = includeAdditionalDeviceFields && (sciDevice || oemDevice) && !visionDevice;
      const esnFlag = includeAdditionalDeviceFields && !sciDevice && !oemDevice;
      const imeiFlag = includeAdditionalDeviceFields && visionDevice;
      setFormState({
        [name] : {
          name,
          value    : isValue(value, 'none') ? null : value,
          required : includeAdditionalDeviceFields && required
        },
        [DeviceFormFields.DeviceConfiguration] : {
          name     : DeviceFormFields.DeviceConfiguration,
          value    : formState[DeviceFormFields.DeviceConfiguration]?.value,
          required : includeAdditionalDeviceFields,
          error    : includeAdditionalDeviceFields
        },
        [DeviceFormFields.ESN] : {
          name     : DeviceFormFields.ESN,
          value    : (!sciDevice || !oemDevice) ? formState[DeviceFormFields.ESN]?.value : '',
          required : !!(esnFlag && !esnVal)
        },
        [DeviceFormFields.SerialNumber] : {
          name     : DeviceFormFields.SerialNumber,
          value    : (sciDevice || oemDevice) ? formState[DeviceFormFields.SerialNumber]?.value : '',
          required : !!(serialNumberFlag && !serialNumberVal)
        },
        [DeviceFormFields.IMEI] : {
          name     : DeviceFormFields.IMEI,
          value    : formState[DeviceFormFields.IMEI]?.value,
          required : !!(imeiFlag && !imeiVal)
        },
        [DeviceFormFields.IMEI2] : {
          name  : DeviceFormFields.IMEI2,
          value : formState[DeviceFormFields.IMEI2]?.value
        }
      });
    } else {
      setFormState({[name]: {name, value: isValue(value, 'none') ? null : value, required, changed}});
    }
  };

  function handleDateChange({name, value, required = false}) {
    setFormState({[name]: {name, value: toISOString(value), required}});
  };

  const handleSwitchChange = function({target: {name, checked}}) {
    setFormState({[name]: {name, value: checked, changed: true, required: formState[name].required}});
  };

  const handleAutoChange = function({target: {name, checked}}) {
    if (autoFlightModeDevice) {
      setFormState({
        [SCIDeviceSettingsFormField.autoFlightMode] : {
          name     : SCIDeviceSettingsFormField.autoFlightMode,
          value    : checked,
          changed  : true,
          required : formState[name].required
        }
      });
    } else {
      setFormState({
        [SCIDeviceSettingsFormField.autoFlightMode] : {
          name     : SCIDeviceSettingsFormField.autoFlightMode,
          value    : checked,
          changed  : true,
          required : formState[name].required
        },
        [SCIDeviceSettingsFormField.pressureSensorFlightMode] : {
          name     : SCIDeviceSettingsFormField.pressureSensorFlightMode,
          value    : checked,
          changed  : true,
          required : formState[name].required
        }
      });
    }
  };

  function onCancelDialog(e) {
    clearForm();
    onCloseDialog?.(e);
  }

  const getSwitchLabel = useCallback(function(entry: string, fields: Array<string>): string {
    const label = translate(`admin-devices:editors.${entry}.label`);
    const isPending = fields.filter(item => formState[item]?.pending);
    const pending = isPending.length ? ` (${translate('common:editors.pending')})`: '';
    return `${label}${pending}`;
  }, [gpsActivated, tagEnabled, oceanMode, autoFlightMode, pressureSensorFlightMode]);

  const onChipDelete = useCallback((selectedGroupId, selectedGroups) => {
    const filteredGroups = selectedGroups?.filter((group) => {
      const groupId = entityId(group);
      return groupId !== selectedGroupId;
    });
    setSelectedGroups(filteredGroups);
    setFormState({groups: {name: 'groups', value: filteredGroups, changed: true, required: false}});
  }, []);

  const onDeleteGroup = useCallback(
    (group: Link) => () => {
      const selectedGroupId = entityId(group);
      onChipDelete(selectedGroupId, selectedGroups);
    }, [selectedGroups]);

  return (
    <EditorDialog open={open}
      dialogTitle={dialogTitle}
      onCloseDialog={onCloseDialog}
      onCancelDialog={onCancelDialog}
      onPrimaryButtonClick={handleSubmit}
      omitActions={isViewMode}
      primaryButtonDisabled={!isValid}
      primaryButtonLoading={loading}>
      <form id={'editor'} className={classes.form} onSubmit={handleSubmit}>
        <div className={classes.formWrapper}>
          <FormSection bottomBorder
            title={translate('common:form.asset.general')}
            legend={enableBulkAdd && !isEditMode && !isViewMode
              ? (
                <div className={classes.bulkAddLegend}>
                  <div className={classes.bulkAddCtaContainer}>
                    <TertiaryButton onClick={onSwitchToBulkAdd}
                      fontSize={'14px'}>
                      {`+ ${translate('admin-devices:dialogBulkAdd.title')}`}
                    </TertiaryButton>
                  </div>
                  <div>
                    {translate('common:editors.shared.fieldsRequired')}
                  </div>
                </div>)
              : translate('common:editors.shared.fieldsRequired')
            }>
            <>
              <FormItem fullWidth>
                <FormTextField required
                  disabled={isViewMode}
                  fieldLabel={translate('admin-devices:dialog.deviceName.label')}
                  placeholder={translate('admin-devices:dialog.deviceName.placeholder')}
                  name={DeviceFormFields.DeviceName}
                  onChange={handleFieldChange}
                  hasError={formState[DeviceFormFields.DeviceName]?.error}
                  inputProps={{maxLength: 32}}
                  errorText={translate('common:editors.shared.required')}
                  defaultValue={formState[DeviceFormFields.DeviceName]?.value} />
              </FormItem>
              <FormItem>
                <GroupChooser required
                  account={account}
                  readOnly={disableAccountChooser}
                  includeGroupProperties={includeGroupProperties}
                  hasError={formState[DeviceFormFields.Account]?.error}
                  defaultValue={formState[DeviceFormFields.Account]?.value || account}
                  onLoad={setGroupsLoading}
                  onChange={function(value) {
                    setGroupValue(value);
                    handleChooserChange({
                      name     : DeviceFormFields.Account,
                      required : true,
                      value
                    });
                  }} />
              </FormItem>
              {includeAdditionalDeviceFields && (
                <><FormItem>
                  <DeviceTypeChooser required
                    readOnly={loading || isViewMode}
                    onChange={function(value) {
                      handleChooserChange({
                        name : DeviceFormFields.DeviceType,
                        value
                      });
                    } }
                    hasError={formState[DeviceFormFields.DeviceType]?.error}
                    defaultValue={formState[DeviceFormFields.DeviceType]?.value?.title || ''} />
                </FormItem>
                <FormItem>
                  <DeviceConfigurationChooser required
                    readOnly={loading || isViewMode || !formState[DeviceFormFields.DeviceType]?.value}
                    accountId={entityId(account)}
                    deviceType={formState[DeviceFormFields.DeviceType]?.value}
                    onChange={function(value) {
                      handleChooserChange({
                        name     : DeviceFormFields.DeviceConfiguration,
                        required : true,
                        value
                      });
                    } }
                    hasError={formState[DeviceFormFields.DeviceConfiguration]?.error}
                    defaultValue={formState[DeviceFormFields.DeviceConfiguration]?.value?.title || ''} />
                </FormItem>
                {checkSCIDevice || checkOEMDevice ? (
                  <FormItem>
                    <FormTextField required={!checkVisionDevice}
                      disabled={isViewMode}
                      fieldLabel={translate('admin-devices:dialog.deviceIdentifier.serialNumber')}
                      placeholder={translate('admin-devices:dialog.deviceIdentifier.serialPlaceholder')}
                      name={DeviceFormFields.SerialNumber}
                      maxLength={32}
                      onChange={handleFieldChange}
                      hasError={formState[DeviceFormFields.SerialNumber]?.error}
                      errorText={translate(!includeAdditionalDeviceFields
                        ? 'common:editors.shared.required'
                        : 'admin-devices:dialog.deviceIdentifier.serialNumberError'
                      )}
                      defaultValue={formState[DeviceFormFields.SerialNumber]?.value || ''} />
                  </FormItem>
                ) : (
                  <FormItem>
                    <FormIntegerField required
                      disabled={isViewMode}
                      fieldLabel={label ? translate('admin-devices:dialog.deviceIdentifier.label')
                        : translate('admin-devices:dialog.deviceIdentifier.esn')}
                      placeholder={label ? translate('admin-devices:dialog.deviceIdentifier.placeholder')
                        : translate('admin-devices:dialog.deviceIdentifier.esnPlaceholder')}
                      name={DeviceFormFields.ESN}
                      maxLength={32}
                      onChange={handleFieldChange}
                      hasError={formState[DeviceFormFields.ESN]?.error}
                      errorText={translate(!includeAdditionalDeviceFields
                        ? 'common:editors.shared.required'
                        : 'admin-devices:dialog.deviceIdentifier.error'
                      )}
                      defaultValue={formState[DeviceFormFields.ESN]?.value || ''} />
                  </FormItem>
                )}
                <FormItem>
                  <FormTextField disabled={isViewMode}
                    fieldLabel={translate('admin-devices:dialog.iccid.label')}
                    placeholder={translate('admin-devices:dialog.iccid.placeholder')}
                    name={DeviceFormFields.ICCID}
                    onChange={handleFieldChange}
                    maxLength={32}
                    hasError={formState[DeviceFormFields.ICCID]?.error}
                    defaultValue={formState[DeviceFormFields.ICCID]?.value} />
                </FormItem>
                <FormItem>
                  <DatePicker closeOnClickOutside
                    useDatePickerTextField
                    fieldLabel={translate('admin-devices:dialog.shipDate.label')}
                    placeholder={translate('admin-devices:dialog.shipDate.placeholder')}
                    disabled={isViewDialog}
                    open={openDatePickers[DeviceFormFields.ShipDate]}
                    toggle={function() {
                      setOpenDatePickers(
                        {[DeviceFormFields.ShipDate]: !openDatePickers[DeviceFormFields.ShipDate]}
                      );
                    } }
                    onChange={function(value) {
                      handleDateChange({
                        name : DeviceFormFields.ShipDate,
                        value
                      });
                    } }
                    defaultValue={formState[DeviceFormFields.ShipDate]?.value} />
                </FormItem>
                <FormItem>
                  <FormTextField disabled={isViewMode}
                    fieldLabel={translate('admin-devices:dialog.isdn.label')}
                    placeholder={translate('admin-devices:dialog.isdn.placeholder')}
                    name={DeviceFormFields.ISDN}
                    onChange={handleFieldChange}
                    maxLength={32}
                    hasError={formState[DeviceFormFields.ISDN]?.error}
                    defaultValue={formState[DeviceFormFields.ISDN]?.value} />
                </FormItem>
                <FormItem>
                  <FormTextField disabled
                    fieldLabel={translate('admin-devices:dialog.airId.label')}
                    placeholder={translate('admin-devices:dialog.airId.placeholder')}
                    name={DeviceFormFields.AirId}
                    onChange={handleFieldChange}
                    hasError={formState[DeviceFormFields.AirId]?.error}
                    defaultValue={formState[DeviceFormFields.AirId]?.value} />
                </FormItem>
                <FormItem>
                  <FormTextField disabled={isViewMode}
                    fieldLabel={translate('admin-devices:dialog.meid.label')}
                    placeholder={translate('admin-devices:dialog.meid.placeholder')}
                    name={DeviceFormFields.MEID}
                    onChange={handleFieldChange}
                    maxLength={32}
                    hasError={formState[DeviceFormFields.MEID]?.error}
                    defaultValue={formState[DeviceFormFields.MEID]?.value} />
                </FormItem>
                <FormItem>
                  <FormTextField required={checkVisionDevice}
                    disabled={isViewMode}
                    fieldLabel={translate('admin-devices:dialog.imei.label')}
                    placeholder={translate('admin-devices:dialog.imei.placeholder')}
                    name={DeviceFormFields.IMEI}
                    onChange={handleFieldChange}
                    maxLength={32}
                    hasError={formState[DeviceFormFields.IMEI]?.error}
                    errorText={translate(!includeAdditionalDeviceFields
                      ? 'common:editors.shared.required'
                      : 'admin-devices:dialog.deviceIdentifier.imeiError'
                    )}
                    defaultValue={formState[DeviceFormFields.IMEI]?.value} />
                </FormItem>
                <FormItem>
                  <FormTextField disabled={isViewMode}
                    fieldLabel={translate('admin-devices:dialog.imei2.label')}
                    placeholder={translate('admin-devices:dialog.imei2.placeholder')}
                    name={DeviceFormFields.IMEI2}
                    onChange={handleFieldChange}
                    maxLength={32}
                    hasError={formState[DeviceFormFields.IMEI2]?.error}
                    defaultValue={formState[DeviceFormFields.IMEI2]?.value} />
                </FormItem></>
              )}
              <FormItem>
                <EntityStatusChooser defaultLabel
                  readOnly={isViewMode}
                  onChange={function(value) {
                    handleChooserChange({
                      name : DeviceFormFields.Status,
                      value
                    });
                  } }
                  status={formState[DeviceFormFields.Status]?.value || EntityStatus.Enabled} />
              </FormItem>
              {isDeviceNotes && (
                <FormItem fullWidth>
                  <FormTextField disabled={isViewMode}
                    fieldLabel={translate('admin-devices:dialog.note.label')}
                    placeholder={translate('admin-devices:dialog.note.placeholder')}
                    name={DeviceFormFields.DeviceNote}
                    onChange={handleFieldChange}
                    maxLength={200}
                    hasError={formState[DeviceFormFields.DeviceNote]?.error}
                    defaultValue={formState[DeviceFormFields.DeviceNote]?.value} />
                </FormItem>
              )}
              {(isEditMode && includeIdentityInformation) && (
                <>
                  <FormItem>
                    <FormTextField name={DeviceFormFields.ESN}
                      fieldLabel={translate('admin-devices:grid.columns.esn')}
                      defaultValue={formState[DeviceFormFields.ESN]?.value}
                      disabled />
                  </FormItem>
                  <FormItem>
                    <FormTextField name={DeviceFormFields.SerialNumber}
                      fieldLabel={translate('admin-devices:grid.columns.serialNumber')}
                      defaultValue={formState[DeviceFormFields.SerialNumber]?.value}
                      disabled />
                  </FormItem>
                </>
              )}
              {(isEditMode && includeDatetimeInformation) && (
                <>
                  <FormItem>
                    <FormTextField name={DeviceFormFields.CreatedOn}
                      fieldLabel={translate('common:editors.labels.createdOn')}
                      defaultValue={formState[DeviceFormFields.CreatedOn]?.value}
                      disabled />
                  </FormItem>
                  <FormItem>
                    <FormTextField name={DeviceFormFields.LastModifiedOn}
                      fieldLabel={translate('common:editors.labels.lastModifiedOn')}
                      defaultValue={formState[DeviceFormFields.LastModifiedOn]?.value}
                      disabled />
                  </FormItem>
                </>)
              }
            </>
          </FormSection>
          {includeSpreaderChooser && !checkSCIDevice && (
            <FormSection bottomBorder
              title={translate('admin-devices:editors.spreaderChooser.label')}>
              <FormItem>
                <SpreaderChooser defaultLabel
                  readOnly={isViewMode}
                  onChange={function(value) {
                    handleChooserChange({
                      name  : DeviceFormFields.Features,
                      value : {
                        ...formState[DeviceFormFields.Features]?.value,
                        [DeviceFormFields.ATTACHMENTS] : getSpreadVal(value)
                      }
                    });
                  } }
                  value={formState[DeviceFormFields.Features]?.value.ATTACHMENTS}
                  multiSelect={isMultiSelectSpreader} />
              </FormItem>
            </FormSection>
          )}
          {(includeSwapDevice && device?.groups?.length) && (
            <DeviceGroupsChip isSwapDevice
              groups={device?.groups} />
          )}
          {includeGPSFields && sciGPSDevice && (<FormSection>
            <FormItem>
              <LabelTypography variantType='label1'>
                {`${translate('common:editors.deviceType.label')}`}
              </LabelTypography>
              <LabelTypography variantType='label2'>
                {`${formState[DeviceFormFields.DeviceType]?.value?.title}`}
              </LabelTypography>
            </FormItem>
            <FormItem>
              <LabelTypography variantType='label1'>
                {`${translate('common:editors.lastReported.label')}`}
              </LabelTypography>
              <LabelTypography variantType='label2'>
                {`${formState[DeviceFormFields.LastReported]?.value}`}
              </LabelTypography>
            </FormItem>
            <FormItem>
              <LabelTypography variantType='label1'>
                {`${translate('common:editors.deviceId.label')}`}
              </LabelTypography>
              <LabelTypography variantType='label2'>
                {`${formState[DeviceFormFields.DeviceId]?.value}`}
              </LabelTypography>
            </FormItem>
            <FormItem>
              <LabelTypography variantType='label1'>
                {`${translate('common:editors.lastAddress.label')}`}
              </LabelTypography>
              <LabelTypography variantType='label2'>
                {`${formState[DeviceFormFields.LastAddress]?.value}`}
              </LabelTypography>
            </FormItem>
            <FormItem>
              <Switch name={SCIDeviceSettingsFormField.GPSActivated}
                label={getSwitchLabel('gpsActivated', [SCIDeviceSettingsFormField.GPSActivated])}
                onChange={handleSwitchChange}
                checked={gpsActivated}/>
              {formState[SCIDeviceSettingsFormField.GPSActivated]?.pending && (
                <Tooltip title={`${translate('admin-devices:editors.gpsActivated.info')}`} arrow>
                  <span><InfoIcon /></span>
                </Tooltip>
              )}
            </FormItem>
            {sciTagDevice && (
              <FormItem>
                <Switch name={SCIDeviceSettingsFormField.iOnTagEnabled}
                  label={getSwitchLabel('iOnTagEnabled', [SCIDeviceSettingsFormField.iOnTagEnabled])}
                  onChange={handleSwitchChange}
                  checked={tagEnabled}/>
                {formState[SCIDeviceSettingsFormField.iOnTagEnabled]?.pending &&(
                  <Tooltip title={`${translate('admin-devices:editors.iOnTagEnabled.info')}`} arrow>
                    <span><InfoIcon /></span>
                  </Tooltip>)}
              </FormItem>
            )}
            {flightModeDevice && (
              <FormItem>
                <Switch name={SCIDeviceSettingsFormField.autoFlightMode}
                  label={getSwitchLabel('flightMode', [SCIDeviceSettingsFormField.autoFlightMode, SCIDeviceSettingsFormField.pressureSensorFlightMode])}
                  onChange={handleAutoChange}
                  checked={autoFlightMode}/>
                {formState[SCIDeviceSettingsFormField.autoFlightMode] &&
                   formState[SCIDeviceSettingsFormField.pressureSensorFlightMode]?.pending &&(
                  <Tooltip title={`${translate('admin-devices:editors.flightMode.info')}`} arrow>
                    <span><InfoIcon /></span>
                  </Tooltip>)}
              </FormItem>
            )}
            {autoFlightModeDevice && (
              <FormItem>
                <Switch name={SCIDeviceSettingsFormField.autoFlightMode}
                  label={getSwitchLabel('flightMode', [SCIDeviceSettingsFormField.autoFlightMode])}
                  onChange={handleAutoChange}
                  checked={autoFlightMode}/>
                {formState[SCIDeviceSettingsFormField.autoFlightMode]?.pending && (
                  <Tooltip title={`${translate('admin-devices:editors.flightMode.info')}`} arrow>
                    <span><InfoIcon /></span>
                  </Tooltip>)}
              </FormItem>
            )}
            {oceanModeDevice && (
              <FormItem>
                <Switch name={SCIDeviceSettingsFormField.oceanMode}
                  label={getSwitchLabel(SCIDeviceSettingsFormField.oceanMode, [SCIDeviceSettingsFormField.oceanMode])}
                  onChange={handleSwitchChange}
                  checked={oceanMode}/>
                {formState[SCIDeviceSettingsFormField.oceanMode]?.pending && (
                  <Tooltip title={`${translate('admin-devices:editors.oceanMode.info')}`} arrow>
                    <span><InfoIcon /></span>
                  </Tooltip>)}
              </FormItem>
            )}
          </FormSection>)}
          {(sciTemperatureDevice || sciMovingDevice) && !showSCISettings && (
            <FormSection bottomBorder={!autoFlightModeDevice}
              title={translate('admin-devices:editors.reportingRate.label')}>
              {sciMovingDevice && (
                <>
                  <FormItem>
                    <ReportingRateChooser fieldLabel={getSwitchLabel(
                      'reportingRateChooser.whenInMotion',
                      [SCIDeviceSettingsFormField.MoveWakeupPeriod]
                    )}
                    readOnly={loading || isViewMode}
                    rate={formState[SCIDeviceSettingsFormField.MoveWakeupPeriod]?.value}
                    name={SCIDeviceSettingsFormField.MoveWakeupPeriod}
                    onChange={handleChooserChange}/>
                  </FormItem>
                  <FormItem>
                    <ReportingRateChooser fieldLabel={getSwitchLabel(
                      'reportingRateChooser.whenInStationary',
                      [SCIDeviceSettingsFormField.WakeupPeriod]
                    )}
                    readOnly={loading || isViewMode}
                    rate={formState[SCIDeviceSettingsFormField.WakeupPeriod]?.value}
                    name={SCIDeviceSettingsFormField.WakeupPeriod}
                    onChange={handleChooserChange}/>
                  </FormItem>
                </>
              )}
              {(sciTemperatureDevice || oceanModeDevice) && (
                <>
                  <FormItem>
                    <ReportingRateChooser fieldLabel={getSwitchLabel(
                      'reportingRateChooser.setTemperatureLogging',
                      [SCIDeviceSettingsFormField.SensorLoggingPeriod]
                    )}
                    readOnly={loading || isViewMode}
                    rate={formState[SCIDeviceSettingsFormField.SensorLoggingPeriod]?.value}
                    name={SCIDeviceSettingsFormField.SensorLoggingPeriod}
                    onChange={handleChooserChange}/>
                  </FormItem>
                </>
              )}
            </FormSection>
          )}
          {(sciTemperatureDevice || sciTagDevice) && !showSCISettings && (
            <FormSection title={translate('admin-devices:editors.threshholds.label')}>
              {sciTemperatureDevice && (<FormItem>
                <DeviceThresholdsChooser fieldLabel={getSwitchLabel(
                  'deviceThresholdsChooser.temperature',
                  [
                    SCIDeviceSettingsFormField.TemperatureAlert,
                    SCIDeviceSettingsFormField.MinTemperature,
                    SCIDeviceSettingsFormField.MaxTemperature
                  ]
                )}
                name={SCIDeviceSettingsFormField.TemperatureAlert}
                checked={temperatureAlert}
                units={SCIDeviceSettingsUnits.Degree}
                onToggleThreshold={handleSwitchChange}
                setIsValid={setIsValid}
                classes={classes}
                firstInputProps={{
                  fieldName : SCIDeviceSettingsFormField.MinTemperature,
                  value     : formState[SCIDeviceSettingsFormField.MinTemperature]?.value,
                  errorMsg  : {
                    Imperial : translate('admin-devices:editors.deviceThresholdsChooser.temperature.errorMsg.Imperial'),
                    Metric   : translate('admin-devices:editors.deviceThresholdsChooser.temperature.errorMsg.Metric')
                  }
                }}
                secondInputProps={{
                  fieldName : SCIDeviceSettingsFormField.MaxTemperature,
                  value     : formState[SCIDeviceSettingsFormField.MaxTemperature]?.value,
                  errorMsg  : translate('admin-devices:editors.deviceThresholdsChooser.temperature.errorMsg')
                }}
                onChange={handleFieldChange}
                systemOfUnits={systemOfUnits}
                readOnly={loading || isViewMode}/>
              </FormItem>)}
              {sciTagDevice &&(<>
                <FormItem>
                  <DeviceThresholdsChooser fieldLabel={getSwitchLabel(
                    'deviceThresholdsChooser.tilt',
                    [
                      SCIDeviceSettingsFormField.TipAlert,
                      SCIDeviceSettingsFormField.MaxTip
                    ]
                  )}
                  name={SCIDeviceSettingsFormField.TipAlert}
                  checked={tipAlert}
                  onToggleThreshold={handleSwitchChange}
                  setIsValid={setIsValid}
                  classes={classes}
                  units={SCIDeviceSettingsUnits.Degree}
                  firstInputProps={{
                    fieldName : SCIDeviceSettingsFormField.MaxTip,
                    value     : formState[SCIDeviceSettingsFormField.MaxTip]?.value,
                    errorMsg  : translate('admin-devices:editors.deviceThresholdsChooser.tilt.errorMsg')
                  }}
                  onChange={handleFieldChange}
                  readOnly={loading || isViewMode}/>
                </FormItem>
                <FormItem>
                  <DeviceThresholdsChooser fieldLabel={getSwitchLabel(
                    'deviceThresholdsChooser.light',
                    [
                      SCIDeviceSettingsFormField.LightLevelAlert,
                      SCIDeviceSettingsFormField.MinLightLevel,
                      SCIDeviceSettingsFormField.MaxLightLevel
                    ]
                  )}
                  name={SCIDeviceSettingsFormField.LightLevelAlert}
                  checked={lightLevelAlert}
                  onToggleThreshold={handleSwitchChange}
                  setIsValid={setIsValid}
                  classes={classes}
                  units={SCIDeviceSettingsUnits.Light}
                  firstInputProps={{
                    fieldName : SCIDeviceSettingsFormField.MinLightLevel,
                    value     : formState[SCIDeviceSettingsFormField.MinLightLevel]?.value,
                    errorMsg  : translate('admin-devices:editors.deviceThresholdsChooser.light.errorMsg')
                  }}
                  secondInputProps={{
                    fieldName : SCIDeviceSettingsFormField.MaxLightLevel,
                    value     : formState[SCIDeviceSettingsFormField.MaxLightLevel]?.value
                  }}
                  onChange={handleFieldChange}
                  readOnly={loading || isViewMode}/>
                </FormItem>
                <FormItem>
                  <DeviceThresholdsChooser fieldLabel={getSwitchLabel(
                    'deviceThresholdsChooser.shock',
                    [
                      SCIDeviceSettingsFormField.ImpactAlert,
                      SCIDeviceSettingsFormField.MaxImpact
                    ]
                  )}
                  name={SCIDeviceSettingsFormField.ImpactAlert}
                  checked={impactAlert}
                  onToggleThreshold={handleSwitchChange}
                  firstInputProps={{
                    fieldName : SCIDeviceSettingsFormField.MaxImpact,
                    value     : formState[SCIDeviceSettingsFormField.MaxImpact]?.value,
                    errorMsg  : translate('admin-devices:editors.deviceThresholdsChooser.shock.errorMsg')
                  }}
                  onChange={handleFieldChange}
                  setIsValid={setIsValid}
                  units={SCIDeviceSettingsUnits.Shock}
                  classes={classes}
                  readOnly={loading || isViewMode}/>
                </FormItem>
              </>)}
            </FormSection>
          )}
          {(!includeSwapDevice && includeGroups) && (
            <DeviceGroupsChip isDelete
              onDelete={onDeleteGroup}
              groups={selectedGroups} />
          )}
        </div>
      </form>
    </EditorDialog>
  );
};
export default DeviceEditor;
